import { Checkbox, type CheckboxProps } from '@affine/component/ui/checkbox';
import { InformationIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type {
  AriaRole,
  CSSProperties,
  FC,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { useId } from 'react';

import * as styles from './streamlain-coachmark-shell.css';

export type StreamLainCoachmarkShellTone = 'neutral' | 'warning';
export type StreamLainCoachmarkTextTone = 'primary' | 'secondary';
export type StreamLainCoachmarkSurface = 'default' | 'disclosure';
export type StreamLainCoachmarkNotchPlacement =
  | 'none'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left';

interface StreamLainCoachmarkShellProps
  extends PropsWithChildren, Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  compact?: boolean;
  notch?: StreamLainCoachmarkNotchPlacement;
  testId?: string;
  scrollable?: boolean;
  surface?: StreamLainCoachmarkSurface;
  tone?: StreamLainCoachmarkShellTone;
}

interface StreamLainCoachmarkMessageProps {
  action?: ReactNode;
  body: ReactNode;
  bodyTone?: StreamLainCoachmarkTextTone;
  icon?: ReactNode;
  title?: ReactNode;
}

interface StreamLainCoachmarkCheckboxRowProps {
  checked: boolean;
  label: string;
  name?: string;
  onChange?: CheckboxProps['onChange'];
}

export const StreamLainCoachmarkShell: FC<StreamLainCoachmarkShellProps> = ({
  children,
  className,
  compact = false,
  notch = 'none',
  role,
  scrollable = false,
  surface = 'default',
  style,
  testId,
  tone = 'neutral',
  ...attrs
}) => {
  const content = scrollable ? (
    <div className={styles.scrollBody}>{children}</div>
  ) : (
    children
  );

  return (
    <div
      {...attrs}
      className={clsx(
        styles.shell,
        styles.shellSurface[surface],
        styles.shellTone[tone],
        compact && styles.compactShell,
        className
      )}
      data-notch={notch}
      data-surface={surface}
      data-testid={testId}
      role={role as AriaRole | undefined}
      style={style as CSSProperties | undefined}
    >
      {notch !== 'none' ? (
        <span
          aria-hidden
          className={clsx(styles.notch, styles.notchPlacement[notch])}
        />
      ) : null}
      {content}
    </div>
  );
};

export const StreamLainCoachmarkMessage: FC<
  StreamLainCoachmarkMessageProps
> = ({
  action,
  body,
  bodyTone = 'primary',
  icon = <InformationIcon />,
  title,
}) => (
  <div className={styles.message}>
    {icon ? (
      <span className={styles.icon} aria-hidden>
        {icon}
      </span>
    ) : null}
    <div className={styles.messageMain}>
      {title ? <strong className={styles.title}>{title}</strong> : null}
      <div className={clsx(styles.text, styles.textTone[bodyTone])}>{body}</div>
    </div>
    {action ? <div className={styles.action}>{action}</div> : null}
  </div>
);

export const StreamLainCoachmarkFooter: FC<PropsWithChildren> = ({
  children,
}) => <div className={styles.footer}>{children}</div>;

export const StreamLainCoachmarkCheckboxRow: FC<
  StreamLainCoachmarkCheckboxRowProps
> = ({ checked, label, name, onChange }) => {
  const generatedName = useId();
  const checkboxName = name ?? generatedName;

  return (
    <Checkbox
      checked={checked}
      className={styles.checkboxRow}
      label={label}
      labelClassName={styles.checkboxRowLabel}
      name={checkboxName}
      onChange={onChange}
    />
  );
};

export const streamLainCoachmarkInlineActionButtonClass =
  styles.inlineActionButton;
